import React, { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  StyledAlertDialogContent,
  StyledAlertDialogOverlay,
} from '@/shared/ui/alert-dialog/styles';
import {
  BonusBanner,
  BonusBannerWrapper,
  BonusContent,
  BonusInfo,
  BonusStatus,
  Wrapper,
} from './styles';
import { Button } from '@/shared/ui/form/button/button';
import {
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
  BoldHeadingPrimary,
  BoldHeadingSenary,
} from '@/shared/ui/typography';
import { useScopedI18n } from '@locales/client';
import { Box } from '@/shared/ui/box/Box';
import { useVisibleState } from '@/shared/hooks/useVisibleState';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';
import { Condition } from '@/shared/ui/condition/condition';
import { getThemeBrand } from '@/shared/hooks/useBrandTheme';
import { createDialogOpenChangeHandler } from '@/shared/libs/dialog-open-change';
import { useFormatAmount } from '@/shared/hooks/useFormatAmount';

export const BonusNotificationModal = () => {
  const formatAmount = useFormatAmount({ mode: 'plain' });
  const loader = getThemeBrand().assets.loader;
  const { notifications, setNotifications } = useNotifications();
  const [status, setStatus] = React.useState({
    success: false,
    error: false,
  });
  const [info, setInfo] = React.useState({
    image: '',
    win: '',
    name: '',
    bonusBalance: '',
    status: '',
    startWager: 0,
  });
  const t = useScopedI18n('bonus');
  const { open, visible, close } = useVisibleState(false);
  const updateInfoAndStatus = (status, setStatus, setInfo, notifications) => {
    const details = notifications?.bonus?.details;
    const bonusBalance = notifications?.bonus?.bonusBalance;
    const startWager = notifications?.bonus?.startWager;
    const error = details.status === 'expired';

    setStatus({ ...status, error });

    setInfo({
      image: details.image,
      status: details.status,
      giftspins: details.giftspins,
      win: details.win,
      name: details.name,
      type: details.type,
      wager: details.wager,
      bonusBalance,
      startWager,
    });

    open();
  };

  useEffect(() => {
    const validStatuses = ['expired', 'giftspinsEnd', 'completed'];
    const currentStatus = notifications?.bonus?.details?.status;

    if (validStatuses.includes(currentStatus)) {
      updateInfoAndStatus(status, setStatus, setInfo, notifications);
    }
  }, [notifications?.bonus?.details?.status]);

  const onClose = () => {
    setNotifications({
      ...notifications,
      bonus: {
        ...notifications?.bonus,
        details: {
          ...notifications?.bonus?.details,
          status: null,
        },
      },
    });
    close();
  };
  return (
    <Dialog.Root
      open={visible}
      onOpenChange={createDialogOpenChangeHandler(open, close)}
    >
      <Dialog.Portal>
        <StyledAlertDialogOverlay onClick={() => null} />
        <StyledAlertDialogContent aria-describedby="" data-item={`bonus_notification_${info.status || 'unknown'}`}>
          <Wrapper $error={status.error}>
            <BonusBannerWrapper>
              <BonusBanner $banner={info.image} $loaderDefault={loader?.default}>
                <Box
                  $zIndex="3"
                  $position={'relative'}
                  $textAlign="center"
                  $marginTop={24}
                >
                  <BoldHeadingSenary>{info.name}</BoldHeadingSenary>
                  <Box $marginTop={32}>
                    <Condition match={info.status === 'expired'}>
                      <Condition match={info.type === 'freespin'}>
                        <BodyTextRegularSecondary>
                          {t('loose_modal.title')}
                        </BodyTextRegularSecondary>
                      </Condition>
                      <Condition match={info.type !== 'freespin'}>
                        <BodyTextRegularSecondary>
                          {t('loose_modal.state')}
                        </BodyTextRegularSecondary>
                      </Condition>
                    </Condition>
                    <Condition match={info.status === 'completed'}>
                      <BodyTextRegularSecondary>
                        {t('win_modal.title')}
                      </BodyTextRegularSecondary>
                    </Condition>
                  </Box>
                  <Box $marginTop={8} $marginBottom={8}>
                    <BoldHeadingPrimary>
                      <Condition
                        match={
                          info.status === 'giftspinsEnd' ||
                          info.status === 'completed' ||
                          info.status === 'expired'
                        }
                      >
                        {formatAmount(info.win)}
                      </Condition>
                    </BoldHeadingPrimary>
                  </Box>
                  <BonusStatus $error={status.error}>
                    <Condition match={info.status === 'expired'}>
                      {t('loose_modal.state')}
                    </Condition>
                    <Condition match={info.status === 'completed'}>
                      {t('win_modal.state_bonus')}
                    </Condition>
                    <Condition match={info.status === 'giftspinsEnd'}>
                      {t('win_modal.state_spin')}
                    </Condition>
                  </BonusStatus>
                </Box>
              </BonusBanner>
            </BonusBannerWrapper>
            <BonusContent>
              <Condition match={info.status === 'expired'}>
                <Condition match={info.type === 'freespin'}>
                  <BonusInfo>
                    <BodyTextBoldSecondary color="#EDEFF5">
                      {t('loose_modal.rest_spin')}
                    </BodyTextBoldSecondary>
                    <BodyTextRegularSecondary color="#AFAFAF">
                      {info.giftspins}
                    </BodyTextRegularSecondary>
                  </BonusInfo>
                </Condition>
                <Condition match={info.type !== 'freespin'}>
                  <BonusInfo>
                    <BodyTextBoldSecondary color="#EDEFF5">
                      {t('loose_modal.rest_wager')}
                    </BodyTextBoldSecondary>
                    <BodyTextRegularSecondary color="#AFAFAF">
                      {formatAmount(info.wager)}
                    </BodyTextRegularSecondary>
                  </BonusInfo>
                </Condition>
              </Condition>
              <Condition match={info.status === 'giftspinsEnd'}>
                <BonusInfo>
                  <BodyTextBoldSecondary color="#EDEFF5">
                    {t('win_modal.rest_wager')}
                  </BodyTextBoldSecondary>
                  <BodyTextRegularSecondary color="#AFAFAF">
                    {formatAmount(info.startWager)}
                  </BodyTextRegularSecondary>
                </BonusInfo>
              </Condition>
              <Box $position={'relative'} $zIndex="2">
                <Button
                  style={{ padding: 0 }}
                  fill
                  onClick={() => onClose()}
                  data-analytics-id={`bonus_notification_${info.status || 'unknown'}`}
                >
                  <Box>
                    <BodyTextBoldSecondary>
                      <Condition
                        match={
                          info.status === 'expired' ||
                          info.status === 'completed' ||
                          info.status === 'giftspinsEnd'
                        }
                      >
                        {t('win_modal.button_close')}
                      </Condition>
                    </BodyTextBoldSecondary>
                  </Box>
                </Button>
              </Box>
            </BonusContent>
          </Wrapper>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
